import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/backend/client';
import { toast } from 'sonner';
import { trackCheckoutInitiated } from '@/lib/analytics';

export type PlanId = 'free' | 'starter' | 'pro' | 'business';

export interface PlanFeatures {
  maxLinks: number;
  premiumThemes: boolean;
  removeBranding: boolean;
  analytics: boolean;
  advancedAnalytics: boolean;
  customDomain: boolean;
  miniStore: boolean;
  emailCollection: boolean;
  scheduling: boolean;
  abTesting: boolean;
  aiChat: boolean;
  /** Monthly AI chat message quota for this plan */
  chatMsgLimit: number;
}

// Monthly AI chat message quotas per plan
export const CHAT_PLAN_LIMITS: Record<PlanId, number> = {
  free:     30,
  starter:  300,
  pro:      1000,
  business: 5000,
};

export const PLAN_FEATURES: Record<PlanId, PlanFeatures> = {
  free: {
    maxLinks: Infinity,
    premiumThemes: false,
    removeBranding: false,
    analytics: false,
    advancedAnalytics: false,
    customDomain: false,
    miniStore: false,
    emailCollection: false,
    scheduling: false,
    abTesting: false,
    aiChat: true,
    chatMsgLimit: CHAT_PLAN_LIMITS.free,
  },
  starter: {
    maxLinks: Infinity,
    premiumThemes: true,
    removeBranding: true,
    analytics: true,
    advancedAnalytics: false,
    customDomain: false,
    miniStore: false,
    emailCollection: true,
    scheduling: false,
    abTesting: false,
    aiChat: true,
    chatMsgLimit: CHAT_PLAN_LIMITS.starter,
  },
  pro: {
    maxLinks: Infinity,
    premiumThemes: true,
    removeBranding: true,
    analytics: true,
    advancedAnalytics: true,
    customDomain: true,
    miniStore: true,
    emailCollection: true,
    scheduling: true,
    abTesting: true,
    aiChat: true,
    chatMsgLimit: CHAT_PLAN_LIMITS.pro,
  },
  business: {
    maxLinks: Infinity,
    premiumThemes: true,
    removeBranding: true,
    analytics: true,
    advancedAnalytics: true,
    customDomain: true,
    miniStore: true,
    emailCollection: true,
    scheduling: true,
    abTesting: true,
    aiChat: true,
    chatMsgLimit: CHAT_PLAN_LIMITS.business,
  },
};

export const PLAN_DETAILS: Record<PlanId, { name: string; price: number }> = {
  free: { name: 'Free', price: 0 },
  starter: { name: 'Starter', price: 12 },
  pro: { name: 'Professional', price: 24 },
  business: { name: 'Business', price: 120 },
};

type SubscriptionSnapshot = {
  plan_id?: string | null;
  status?: string | null;
  current_period_end?: string | null;
};

function hasActiveEntitlement(subscription: SubscriptionSnapshot | null | undefined): boolean {
  if (!subscription || subscription.plan_id === 'free') {
    return false;
  }

  if (!subscription.status || !['active', 'trialing'].includes(subscription.status)) {
    return false;
  }

  if (!subscription.current_period_end) {
    return true;
  }

  const endDate = new Date(subscription.current_period_end);
  return !Number.isNaN(endDate.getTime()) && endDate > new Date();
}

function getEffectivePlanId(subscription: SubscriptionSnapshot | null | undefined): PlanId {
  if (!hasActiveEntitlement(subscription)) {
    return 'free';
  }

  const rawPlanId = subscription?.plan_id;
  if (rawPlanId === 'starter' || rawPlanId === 'pro' || rawPlanId === 'business') {
    return rawPlanId;
  }

  return 'free';
}

interface SubscriptionContextType {
  planId: PlanId;
  features: PlanFeatures;
  subscriptionEnd: string | null;
  cancelAtPeriodEnd: boolean;
  hasStripeCustomer: boolean;
  loading: boolean;
  isCheckoutLoading: boolean;
  checkoutLoadingPlanId: 'starter' | 'pro' | 'business' | null;
  checkSubscription: () => Promise<void>;
  createCheckout: (planId: 'starter' | 'pro' | 'business', billing?: 'monthly' | 'annual') => Promise<void>;
  createGuestCheckout: (planId: 'starter' | 'pro' | 'business', billing?: 'monthly' | 'annual') => Promise<void>;
  openCustomerPortal: () => Promise<void>;
  canUseFeature: (feature: keyof PlanFeatures) => boolean;
}

const PROMO_STORAGE_KEY = 'qlivar_ref';

/** Read ?promo= from current URL and persist to localStorage (first-write wins within 30 days). */
function capturePromoFromURL() {
  try {
    const promo = new URLSearchParams(window.location.search).get('promo')?.trim();
    if (!promo || promo.length < 3 || promo.length > 30) return;
    const existing = localStorage.getItem(PROMO_STORAGE_KEY);
    if (existing) {
      try {
        const parsed = JSON.parse(existing);
        // Keep existing promo if still within 30 days
        if (parsed?.code && parsed?.ts && Date.now() - parsed.ts < 30 * 24 * 60 * 60 * 1000) return;
      } catch { /* overwrite corrupt data */ }
    }
    localStorage.setItem(PROMO_STORAGE_KEY, JSON.stringify({ code: promo.toUpperCase(), ts: Date.now() }));
  } catch { /* localStorage unavailable */ }
}

/** Retrieve saved promo code (valid for 30 days). */
function getSavedPromoCode(): string | undefined {
  try {
    const raw = localStorage.getItem(PROMO_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (parsed?.code && parsed?.ts && Date.now() - parsed.ts < 30 * 24 * 60 * 60 * 1000) {
      return parsed.code;
    }
    localStorage.removeItem(PROMO_STORAGE_KEY);
  } catch { /* ignore */ }
  return undefined;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user, session } = useAuth();
  const [planId, setPlanId] = useState<PlanId>('free');
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [hasStripeCustomer, setHasStripeCustomer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkoutLoadingPlanId, setCheckoutLoadingPlanId] = useState<'starter' | 'pro' | 'business' | null>(null);
  const checkoutInFlightRef = useRef(false);

  // Capture ?promo= from URL on first render
  useEffect(() => { capturePromoFromURL(); }, []);

  const checkSubscription = useCallback(async () => {
    if (!user || !session) {
      setPlanId('free');
      setHasStripeCustomer(false);
      setLoading(false);
      return;
    }

    // Em ambiente local, buscar diretamente do banco de dados
    const isLocal = import.meta.env.VITE_SUPABASE_URL?.includes('127.0.0.1');
    
    if (isLocal) {
      try {
        const { data: subscription, error } = await supabase
          .from('subscriptions')
          .select('plan_id, status, current_period_end, cancel_at_period_end, stripe_customer_id')
          .eq('user_id', user.id)
          .single();

        if (error || !subscription) {
          setPlanId('free');
          setHasStripeCustomer(false);
        } else {
          // Em local dev, confiar diretamente em plan_id + status (sem verificar expiração)
          // Evita que assinaturas de teste expirem após 30 dias tornando-se 'free'
          const validPlans: PlanId[] = ['free', 'starter', 'pro', 'business'];
          const isActive = ['active', 'trialing'].includes(subscription.status || '');
          const rawPlan = subscription.plan_id as string;
          const effectivePlanId: PlanId =
            isActive && validPlans.includes(rawPlan as PlanId) ? (rawPlan as PlanId) : 'free';
          setPlanId(effectivePlanId);
          setSubscriptionEnd(effectivePlanId === 'free' ? null : subscription.current_period_end || null);
          setCancelAtPeriodEnd(effectivePlanId === 'free' ? false : subscription.cancel_at_period_end || false);
          setHasStripeCustomer(!!subscription.stripe_customer_id);
        }
      } catch (error: unknown) {
        console.warn('Could not check subscription, defaulting to free:', error instanceof Error ? error.message : error);
        setPlanId('free');
        setHasStripeCustomer(false);
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (error) {
        console.warn('Could not check subscription, defaulting to free:', error.message);
        setPlanId('free');
        setHasStripeCustomer(false);
        return;
      }

      if (data) {
        setPlanId((data.plan_id as PlanId) || 'free');
        setSubscriptionEnd(data.subscription_end || null);
        setCancelAtPeriodEnd(data.cancel_at_period_end || false);
        setHasStripeCustomer(data.has_stripe_customer || false);
      }
    } catch (error: unknown) {
      console.warn('Could not check subscription, defaulting to free:', error instanceof Error ? error.message : error);
      setPlanId('free');
      setHasStripeCustomer(false);
    } finally {
      setLoading(false);
    }
  }, [user, session]);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  // Refresh subscription status periodically
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      checkSubscription();
    }, 60000); // Every minute

    return () => clearInterval(interval);
  }, [user, checkSubscription]);

  const getCheckoutCurrency = (): 'usd' | 'brl' => {
    const lang = localStorage.getItem('qlivar_language');
    return lang === 'pt' ? 'brl' : 'usd';
  };

  const createCheckout = async (selectedPlanId: 'starter' | 'pro' | 'business', billing: 'monthly' | 'annual' = 'monthly') => {
    if (!user) {
      toast.error('Faça login para assinar');
      return;
    }

    if (checkoutInFlightRef.current) {
      return;
    }

    checkoutInFlightRef.current = true;
    setCheckoutLoadingPlanId(selectedPlanId);

    // Track checkout initiation in GA4
    trackCheckoutInitiated(selectedPlanId);

    try {
      const currency = getCheckoutCurrency();
      const promoCode = getSavedPromoCode();
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { planId: selectedPlanId, currency, billing, ...(promoCode ? { promoCode } : {}) },
      });

      if (error) throw error;

      if (data?.trial_eligible === false) {
        toast.info('Você já utilizou o período de teste gratuito para este plano. A cobrança será imediata.', {
          duration: 6000,
        });
      }

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Falha ao iniciar checkout. Tente novamente.');
    } finally {
      checkoutInFlightRef.current = false;
      setCheckoutLoadingPlanId(null);
    }
  };

  const createGuestCheckout = async (selectedPlanId: 'starter' | 'pro' | 'business', billing: 'monthly' | 'annual' = 'monthly') => {
    if (checkoutInFlightRef.current) return;

    checkoutInFlightRef.current = true;
    setCheckoutLoadingPlanId(selectedPlanId);
    trackCheckoutInitiated(selectedPlanId);

    try {
      const currency = getCheckoutCurrency();
      const promoCode = getSavedPromoCode();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ planId: selectedPlanId, currency, billing, ...(promoCode ? { promoCode } : {}) }),
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Checkout failed');

      if (data?.trial_eligible === false) {
        toast.info('Você já utilizou o período de teste gratuito para este plano. A cobrança será imediata.', {
          duration: 6000,
        });
      }

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error creating guest checkout:', error);
      toast.error('Falha ao iniciar checkout. Tente novamente.');
    } finally {
      checkoutInFlightRef.current = false;
      setCheckoutLoadingPlanId(null);
    }
  };

  const openCustomerPortal = async () => {
    if (!user) {
      toast.error('Faça login para gerenciar sua assinatura');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');

      if (error) {
        // Try to surface JSON error from the function (non-2xx responses)
        const anyErr = error as Record<string, unknown>;
        const ctx = anyErr?.context as Record<string, unknown> | undefined;
        const body = ctx?.body;
        if (typeof body === 'string') {
          try {
            const parsed = JSON.parse(body);
            const msg = parsed?.error || parsed?.message;
            if (msg) throw new Error(msg);
          } catch {
            // ignore parse errors
          }
        }

        throw error;
      }

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      const message = error instanceof Error ? error.message : 'Failed to open billing portal. Please try again.';
      toast.error(message);
    }
  };

  const canUseFeature = (feature: keyof PlanFeatures): boolean => {
    const features = PLAN_FEATURES[planId];
    const value = features[feature];
    
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value > 0;
    return false;
  };

  return (
    <SubscriptionContext.Provider
      value={{
        planId,
        features: PLAN_FEATURES[planId],
        subscriptionEnd,
        cancelAtPeriodEnd,
        hasStripeCustomer,
        loading,
        isCheckoutLoading: checkoutLoadingPlanId !== null,
        checkoutLoadingPlanId,
        checkSubscription,
        createCheckout,
        createGuestCheckout,
        openCustomerPortal,
        canUseFeature,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
